/**
 * NewExportOrderModal — Feb 2026
 * ------------------------------------------------------------------
 * Admin-only "New Export Order" flow — cuts a customer order stamped
 * as an international export shipment. Visible in Admin → Orders →
 * Customer Orders view when the acting admin has `orders.create`.
 *
 * Differs from `NewCustomerOrderModal` in three ways:
 *   1. Customer is captured inline (name / business / address / email)
 *      instead of picked from the `customer_profiles` list — export
 *      buyers are usually one-off international consignees.
 *   2. Two extra required fields:
 *        • Export To — country dropdown (ISO 3166-1 alpha-2).
 *        • Phone Number — free text; a country-specific sample number
 *          is shown as a placeholder hint. NO validation applied.
 *   3. On submit, the payload carries `is_export_order: true` and the
 *      export_* fields so the order-detail view can badge/display them.
 *
 * The product picker uses the exact same list passed in from the
 * Admin Orders page — which is already category-scoped for sub-admins
 * (via `/api/admin/products`). Storefront customers are never shown
 * this modal.
 */
import React, { useMemo, useState } from 'react';
import axios from 'axios';
import { Plus, Search, X, User, Package, Globe2, UserCheck, UserPlus, Lock } from 'lucide-react';
import { API } from '../context/AdminAuthContext';
import { useToast } from '../hooks/use-toast';
import CustomerNameLabel from './CustomerNameLabel';
import { EXPORT_COUNTRIES, findCountryByCode } from '../data/countries';

const priceOf = (p) => Number(p?.price ?? p?.base_price ?? 0) || 0;
const availableOf = (p) => {
  if (!p) return 0;
  const v = (p.available_qty !== undefined && p.available_qty !== null)
    ? p.available_qty
    : p.stock_qty;
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
};

// Aug 2026 — resolve the effective (customer-aware) price + currency
// for a product. Falls back to the product's default when no manual
// price is set for the picked export customer.
const resolvePrice = (product, manualPriceMap) => {
  const manual = manualPriceMap?.[product?.id];
  if (manual && Number.isFinite(Number(manual.price))) {
    return {
      price: Number(manual.price),
      currency: (manual.currency || 'OMR').toUpperCase(),
      isManual: true,
      defaultPrice: priceOf(product),
    };
  }
  return {
    price: priceOf(product),
    currency: 'OMR',
    isManual: false,
    defaultPrice: priceOf(product),
  };
};

const NewExportOrderModal = ({ products, onClose, onCreated, authHeaders, isSuperAdmin = false }) => {
  const { toast } = useToast();
  // Feb 2026 — Mandatory product-category radio at the top of the
  // modal. One category per order — the product picker + cart are
  // filtered to this category so cross-category orders are
  // impossible. Values must match `products[i].category_slug` on
  // the catalog (server-side categories collection).
  const EXPORT_ORDER_CATEGORIES = [
    { slug: 'ice-cream', label: 'Ice-cream' },
    { slug: 'wafers',    label: 'Wafers' },
    { slug: 'water',     label: 'Water' },
    { slug: 'mc-ex',     label: 'MC-EX' },
  ];
  const [orderCategory, setOrderCategory] = useState('');
  const [buyerMode, setBuyerMode] = useState('existing'); // 'existing' | 'new'
  const [buyer, setBuyer] = useState({
    name: '',
    business_name: '',
    email: '',
    address: '',
  });
  const [countryCode, setCountryCode] = useState('');
  const [phone, setPhone] = useState('');
  const [productQuery, setProductQuery] = useState('');
  const [cart, setCart] = useState({});
  const [submitting, setSubmitting] = useState(false);
  // Feb 2026 — Existing-customer lookup wiring. When the admin picks
  // a saved master, we stash its id + number here so the order POST
  // carries `export_customer_id` and the backend hydrates the rest.
  const [selectedCustomerId, setSelectedCustomerId] = useState('');
  const [selectedCustomerNumber, setSelectedCustomerNumber] = useState('');
  const [lookupQ, setLookupQ] = useState('');
  const [lookupResults, setLookupResults] = useState([]);
  const [lookupBusy, setLookupBusy] = useState(false);
  // Aug 2026 — Manual price overrides for the picked export customer.
  // Loaded from `/api/admin/export-customers/:id/manual-prices/map`
  // whenever `selectedCustomerId` changes so the picker + cart display
  // (and the submitted line prices) use the customer-specific value.
  const [manualPriceMap, setManualPriceMap] = useState({});

  // Aug 2026 — Whenever the selected export customer changes, fetch
  // that customer's manual price map so the picker + cart show the
  // customer-specific price with a "Custom" badge. Clearing the
  // customer (or switching to New) resets the map so we fall back to
  // product default pricing.
  React.useEffect(() => {
    if (!selectedCustomerId) {
      setManualPriceMap({});
      return;
    }
    let cancel = false;
    (async () => {
      try {
        const { data } = await axios.get(
          `${API}/admin/export-customers/${selectedCustomerId}/manual-prices/map`,
          { headers: authHeaders },
        );
        if (!cancel) setManualPriceMap(data?.prices || {});
      } catch {
        if (!cancel) setManualPriceMap({});
      }
    })();
    return () => { cancel = true; };
  }, [selectedCustomerId, authHeaders]);

  const selectedCountry = useMemo(() => findCountryByCode(countryCode), [countryCode]);

  // Debounced lookup — triggered whenever the search box changes and
  // the admin is on the "Existing customer" tab. Hits the backend
  // list endpoint (which also honours numeric-suffix search like
  // "042" → "EXP-000042"). Guarded by a 250 ms delay to avoid
  // hammering Mongo on every keystroke.
  React.useEffect(() => {
    if (buyerMode !== 'existing') return undefined;
    const q = (lookupQ || '').trim();
    let cancel = false;
    const timer = setTimeout(async () => {
      setLookupBusy(true);
      try {
        const { data } = await axios.get(`${API}/admin/export-customers`, {
          headers: authHeaders,
          params: q ? { q } : {},
        });
        if (!cancel) setLookupResults(data?.customers || []);
      } catch {
        if (!cancel) setLookupResults([]);
      } finally {
        if (!cancel) setLookupBusy(false);
      }
    }, 250);
    return () => { cancel = true; clearTimeout(timer); };
  }, [buyerMode, lookupQ, authHeaders]);

  // When the admin picks a master, hydrate every buyer-facing field so
  // the ops-facing summary + hint UX matches what the backend will use.
  const pickMaster = (row) => {
    setSelectedCustomerId(row.id);
    setSelectedCustomerNumber(row.customer_number || '');
    setBuyer({
      name: row.name || '',
      business_name: row.business_name || '',
      email: row.email || '',
      address: row.address || '',
    });
    setCountryCode(row.country_code || '');
    setPhone(row.phone || '');
  };

  const clearMaster = () => {
    setSelectedCustomerId('');
    setSelectedCustomerNumber('');
    setBuyer({ name: '', business_name: '', email: '', address: '' });
    setCountryCode('');
    setPhone('');
  };

  const switchMode = (m) => {
    if (m === buyerMode) return;
    // Feb 2026 — Non-super users can never switch INTO the New
    // customer tab; keep them on Existing.
    if (m === 'new' && !isSuperAdmin) return;
    setBuyerMode(m);
    // Reset both flows on switch so a half-typed New entry doesn't leak
    // into the Existing-tab selection (and vice versa).
    clearMaster();
    setLookupQ('');
    setLookupResults([]);
  };

  // When the country changes, prefill the phone input with the dial
  // code so ops just have to append the local number.
  const onCountryChange = (code) => {
    setCountryCode(code);
    const meta = findCountryByCode(code);
    if (meta && (!phone || phone.trim() === '' || phone.startsWith('+'))) {
      setPhone(`${meta.dial} `);
    }
  };

  const filteredProducts = useMemo(() => {
    // Feb 2026 — Category-scoped picker. Products outside the
    // chosen order category never surface, so the "one category
    // per order" rule holds without extra runtime checks on add.
    if (!orderCategory) return [];
    const inCat = products.filter((p) => (p.category_slug || '') === orderCategory);
    const q = productQuery.trim().toLowerCase();
    if (!q) return inCat.slice(0, 30);
    return inCat.filter((p) => (
      (p.sku || '').toLowerCase().includes(q)
      || (p.name || '').toLowerCase().includes(q)
      || (p.item_code || '').toLowerCase().includes(q)
    )).slice(0, 30);
  }, [productQuery, products, orderCategory]);

  const productsById = useMemo(() => {
    const map = {};
    products.forEach((p) => { map[p.id] = p; });
    return map;
  }, [products]);

  const cartRows = Object.entries(cart)
    .map(([pid, qty]) => ({ product: productsById[pid], qty: Number(qty) }))
    .filter((r) => r.product && r.qty >= 0);

  const cartRowsWithAvail = cartRows.map((r) => {
    const avail = availableOf(r.product);
    const pricing = resolvePrice(r.product, manualPriceMap);
    return { ...r, avail, over: r.qty > avail, pricing };
  });
  const hasOverCap = cartRowsWithAvail.some((r) => r.over);
  const hasAnyValidQty = cartRows.some((r) => r.qty > 0);
  // Aug 2026 — subtotal now respects per-customer manual prices.
  // Currency is displayed per line in the cart; the header total
  // still surfaces in OMR (matches the order-currency assumption
  // elsewhere in the flow — mixed currency is a future concern).
  const subtotal = cartRowsWithAvail.reduce(
    (acc, r) => acc + r.pricing.price * r.qty,
    0,
  );

  const addToCart = (product) => {
    // Feb 2026 — Belt-and-braces category enforcement. The picker
    // already filters by `orderCategory` but this guard protects
    // any programmatic caller (e.g. a future bulk-add flow) from
    // silently mixing categories on a single order.
    if (orderCategory && (product?.category_slug || '') !== orderCategory) {
      toast({
        title: 'Category mismatch',
        description: `This order is locked to ${orderCategory}. "${product?.name || 'item'}" belongs to a different category.`,
      });
      return;
    }
    setCart((prev) => ({ ...prev, [product.id]: (prev[product.id] || 0) + 1 }));
  };
  // Feb 2026 — Allow multi-digit manual entry without treating an
  // in-progress empty string as "remove item". We keep the raw text
  // in state per row; committing to a numeric qty happens on blur or
  // when the user hits Enter. This unblocks flows where ops needs to
  // type e.g. "25" but the previous logic reset the row after the
  // first digit erased the placeholder.
  const setQty = (pid, qty) => {
    // Accept raw string too; strip non-digits.
    const raw = String(qty ?? '').replace(/[^0-9]/g, '');
    if (raw === '') {
      // Keep the row alive with 0; UI shows empty, canSubmit blocks.
      setCart((prev) => ({ ...prev, [pid]: 0 }));
      return;
    }
    const n = Math.max(0, parseInt(raw, 10));
    setCart((prev) => ({ ...prev, [pid]: n }));
  };
  const commitQty = (pid) => {
    // On blur, if the qty is 0 or NaN, drop the item.
    setCart((prev) => {
      const cur = prev[pid];
      if (!cur || cur <= 0) {
        const next = { ...prev };
        delete next[pid];
        return next;
      }
      return prev;
    });
  };
  const removeFromCart = (pid) => setCart((prev) => {
    const next = { ...prev };
    delete next[pid];
    return next;
  });

  // Feb 2026 — Change the mandatory order category. Confirms first
  // whenever the cart has items so ops never accidentally drops
  // work-in-progress lines. Product query is reset too — the old
  // query wouldn't match anything in the new category.
  const pickOrderCategory = (slug) => {
    if (slug === orderCategory) return;
    const cartHasItems = Object.values(cart).some((q) => Number(q) > 0);
    if (cartHasItems) {
      const ok = window.confirm(
        'Switching category will clear the current cart items. Continue?'
      );
      if (!ok) return;
    }
    setOrderCategory(slug);
    setCart({});
    setProductQuery('');
  };

  const buyerReady = !!orderCategory
    && buyer.name.trim().length > 0
    && !!countryCode
    && (buyerMode !== 'existing' || !!selectedCustomerId);
  // Feb 2026 — Stock shortfalls NO LONGER block submission.
  // The order can be placed with over-cap lines; on submit, the
  // backend auto-fires the "Notify Plant" escalation for each
  // shortfall line so production is looped in immediately. The
  // amber warning below still surfaces the risk to the user.
  const canSubmit = buyerReady && hasAnyValidQty && !submitting;

  // Feb 2026 — Mobile 3-step wizard (Customer → Items → Ship & Save).
  // The desktop layout still renders every section inline; on
  // ≤767px we hide non-current sections via `data-mobile-step` and
  // the corresponding CSS in index.css.
  const [mobileStep, setMobileStep] = useState(0);
  const mobileSteps = ['Customer', 'Items', 'Ship & Save'];
  const mobileCanAdvance = mobileStep === 0
    ? buyerReady
    : mobileStep === 1
      ? hasAnyValidQty
      : canSubmit;

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const items = cartRows.filter((r) => r.qty > 0).map((r) => {
        // Aug 2026 — apply the customer-specific manual price when set,
        // otherwise the product's default. This is the value stored on
        // the order line and surfaced on the invoice.
        const pricing = resolvePrice(r.product, manualPriceMap);
        return {
          id: r.product.id,
          name: r.product.name,
          sku: r.product.sku || r.product.item_code || '',
          size: r.product.size || '',
          size_type: r.product.size_type || 'size',
          price: pricing.price,
          qty: r.qty,
          image: r.product.image || '',
          vat_applicable: !!r.product.vat_applicable,
          uom: r.product.uom || 'ctn',
        };
      });
      const country = selectedCountry;
      // Ensure we have a master record before placing the order — in
      // "New customer" mode we lazily create one so the buyer becomes
      // reusable next time (per user requirement Feb 2026). Existing
      // mode already has an id from the picker.
      let masterId = selectedCustomerId;
      let masterNumber = selectedCustomerNumber;
      if (buyerMode === 'new' && !masterId) {
        // Only include email in the master body when the caller typed
        // one — otherwise the backend will accept a phone-only master.
        const emailLc = buyer.email.trim().toLowerCase();
        const masterBody = {
          name: buyer.name.trim(),
          business_name: buyer.business_name.trim(),
          address: buyer.address.trim(),
          country_code: countryCode,
          country_name: country?.name || '',
          phone_dial_code: country?.dial || '',
          phone: phone.trim(),
        };
        if (emailLc) masterBody.email = emailLc;
        try {
          const { data: master } = await axios.post(
            `${API}/admin/export-customers`,
            masterBody,
            { headers: authHeaders },
          );
          masterId = master.id;
          masterNumber = master.customer_number;
          toast({
            title: 'Export customer saved',
            description: `${masterNumber} · reusable for future orders`,
          });
        } catch (e) {
          // Email conflict → fetch the existing master and reuse it.
          if (e?.response?.status === 409 && emailLc) {
            try {
              const { data: existing } = await axios.get(
                `${API}/admin/export-customers/lookup/${encodeURIComponent(emailLc)}`,
                { headers: authHeaders },
              );
              masterId = existing.id;
              masterNumber = existing.customer_number;
              toast({
                title: 'Existing customer reused',
                description: `${masterNumber} matched by email`,
              });
            } catch (le) {
              toast({
                title: 'Customer save failed',
                description: le?.response?.data?.detail || le?.message,
              });
              setSubmitting(false);
              return;
            }
          } else {
            toast({
              title: 'Customer save failed',
              description: e?.response?.data?.detail || e?.message,
            });
            setSubmitting(false);
            return;
          }
        }
      }
      const emailTrimmed = buyer.email.trim();
      const body = {
        customer_name: buyer.name.trim(),
        customer_business_name: buyer.business_name.trim(),
        customer_phone: phone.trim(),
        customer_address: buyer.address.trim(),
        notes: `Export order → ${country?.name || countryCode}`,
        items,
        status: 'confirmed',
        // Export-specific fields — persisted server-side and shown on
        // the admin order detail card.
        is_export_order: true,
        export_country_code: countryCode,
        export_country_name: country?.name || '',
        export_phone_dial_code: country?.dial || '',
        export_customer_id: masterId,
        export_customer_number: masterNumber,
      };
      // Feb 2026 — only send `customer_email` when we actually have
      // one. An empty string still fails Pydantic's EmailStr validator
      // (must be a real address) so we must OMIT the key entirely to
      // let the backend's `Optional[EmailStr] = None` default kick in.
      if (emailTrimmed) body.customer_email = emailTrimmed;
      const { data } = await axios.post(`${API}/admin/orders`, body, { headers: authHeaders });
      toast({
        title: 'Export order created',
        description: `${data?.order_number || ''} · ${masterNumber || country?.name || 'Export'} · ${items.length} item${items.length === 1 ? '' : 's'}`,
      });
      onCreated?.(data);
    } catch (e) {
      // Feb 2026 — FastAPI validation failures arrive as
      // `detail: [{loc, msg, ...}, ...]` which React can't render.
      // Flatten to a readable string here (and the toaster itself
      // has a second coercion layer for defence in depth).
      const raw = e?.response?.data?.detail;
      let description;
      if (Array.isArray(raw)) {
        description = raw
          .map((d) => (d && typeof d === 'object' ? d.msg || JSON.stringify(d) : String(d)))
          .filter(Boolean)
          .join('; ');
      } else if (raw !== undefined && raw !== null) {
        description = typeof raw === 'string' ? raw : String(raw);
      } else {
        description = e?.message || 'Try again.';
      }
      toast({
        title: 'Order create failed',
        description,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/40 z-50 flex md:items-center md:justify-center md:p-4"
      data-testid="new-export-order-modal"
      data-mobile-step={mobileStep}
    >
      <div className="bg-white w-full h-full md:h-auto md:rounded-2xl md:max-w-2xl md:max-h-[90vh] flex flex-col">
        <div className="p-4 md:p-5 border-b border-neutral-100 flex items-center justify-between gap-4 sticky top-0 bg-white z-10">
          <div className="min-w-0">
            <div className="text-xs uppercase tracking-wider font-semibold text-neutral-500 inline-flex items-center gap-1.5">
              <Globe2 className="w-3.5 h-3.5" /> New Export Order
            </div>
            <div className="font-display text-base md:text-lg font-bold text-[#0F172A] truncate">
              {selectedCountry ? `Shipping to ${selectedCountry.name}` : 'International customer order'}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-neutral-400 hover:text-neutral-700 p-1"
            data-testid="new-export-order-close-btn"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Mobile-only step indicator */}
        <div className="md:hidden px-4 pt-3 pb-2 border-b border-neutral-100" data-testid="new-export-order-mobile-stepper">
          <div className="flex items-center gap-2">
            {mobileSteps.map((label, i) => (
              <div key={label} className="flex-1">
                <div className={`h-1.5 rounded-full ${i <= mobileStep ? 'bg-[#0078D4]' : 'bg-neutral-200'}`} />
              </div>
            ))}
          </div>
          <div className="mt-2 flex items-center justify-between text-[11px] font-semibold uppercase tracking-widest">
            <span className="text-neutral-500">Step {mobileStep + 1} of {mobileSteps.length}</span>
            <span className="text-[#0F172A]">{mobileSteps[mobileStep]}</span>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4 md:p-5 space-y-4 pb-28 md:pb-5" data-mobile-step-content={mobileStep}>
          {/* Step 0a — Mandatory product category (Feb 2026).
              One category per order — the picker + cart are scoped
              to whichever category the admin locks in here. */}
          <section data-testid="new-export-order-category-section" data-mobile-step="0">
            <div className="text-xs uppercase tracking-wider font-bold text-neutral-500 flex items-center gap-1.5 mb-2">
              <Package className="w-3.5 h-3.5" /> Product Category
              <span className="text-[10px] font-bold text-red-600">*</span>
            </div>
            <div
              role="radiogroup"
              aria-label="Order product category"
              className="flex flex-wrap gap-2"
              data-testid="new-export-order-category-radios"
            >
              {EXPORT_ORDER_CATEGORIES.map((c) => {
                const active = orderCategory === c.slug;
                return (
                  <label
                    key={c.slug}
                    className={`inline-flex items-center gap-1.5 cursor-pointer text-xs font-semibold px-3 py-1.5 rounded-full border transition-all select-none ${
                      active
                        ? 'bg-[#0078D4] text-white border-[#0078D4] shadow-sm'
                        : 'bg-white text-neutral-700 border-neutral-300 hover:border-[#0078D4] hover:text-[#0078D4]'
                    }`}
                    data-testid={`new-export-order-category-${c.slug}`}
                  >
                    <input
                      type="radio"
                      name="new-export-order-category"
                      className="sr-only"
                      checked={active}
                      onChange={() => pickOrderCategory(c.slug)}
                    />
                    <span
                      aria-hidden
                      className={`w-2.5 h-2.5 rounded-full border ${
                        active ? 'bg-white border-white' : 'border-neutral-400'
                      }`}
                    />
                    {c.label}
                  </label>
                );
              })}
            </div>
            {!orderCategory && (
              <div className="mt-2 text-[11px] text-neutral-500 italic">
                Pick one category to unlock the rest of the form. Cross-category orders are not allowed.
              </div>
            )}
            {orderCategory && (
              <div
                className="mt-2 text-[11px] text-neutral-600 inline-flex items-center gap-1.5 bg-emerald-50 border border-emerald-200 rounded-lg px-2 py-1"
                data-testid="new-export-order-category-locked"
              >
                <Lock className="w-3 h-3 text-emerald-700" />
                Order locked to <b className="text-emerald-800">{
                  EXPORT_ORDER_CATEGORIES.find((c) => c.slug === orderCategory)?.label
                }</b>. Only products in this category appear below.
              </div>
            )}
          </section>

          {/* Step 0 — Existing vs New customer toggle (Feb 2026).
              Only super admins can create a NEW export customer
              inline. Everyone else stays on Existing-customer mode
              — the switcher collapses to a single, non-interactive
              label so the flow stays visually consistent. */}
          <section data-testid="new-export-order-mode-switcher" data-mobile-step="0">
            {isSuperAdmin ? (
              <div className="inline-flex rounded-full bg-neutral-100 p-1 gap-1">
                <button
                  type="button"
                  onClick={() => switchMode('existing')}
                  className={`px-3 py-1 text-xs font-semibold rounded-full transition inline-flex items-center gap-1 ${
                    buyerMode === 'existing' ? 'bg-white text-[#0078D4] shadow-sm' : 'text-neutral-600 hover:text-[#0F172A]'
                  }`}
                  data-testid="new-export-order-mode-existing"
                >
                  <UserCheck className="w-3 h-3" /> Existing customer
                </button>
                <button
                  type="button"
                  onClick={() => switchMode('new')}
                  className={`px-3 py-1 text-xs font-semibold rounded-full transition inline-flex items-center gap-1 ${
                    buyerMode === 'new' ? 'bg-white text-[#0078D4] shadow-sm' : 'text-neutral-600 hover:text-[#0F172A]'
                  }`}
                  data-testid="new-export-order-mode-new"
                >
                  <UserPlus className="w-3 h-3" /> New customer
                </button>
              </div>
            ) : (
              <div
                className="inline-flex items-center gap-1.5 rounded-full bg-neutral-100 px-3 py-1 text-xs font-semibold text-neutral-700"
                data-testid="new-export-order-mode-existing-only"
                title="Only super admins can create new customers during the export order flow."
              >
                <UserCheck className="w-3 h-3 text-[#0078D4]" />
                Existing customer
              </div>
            )}
          </section>

          {/* Step 1a — Existing customer lookup */}
          {buyerMode === 'existing' && !selectedCustomerId && (
            <section data-testid="new-export-order-lookup-section" data-mobile-step="0">
              <div className="text-xs uppercase tracking-wider font-bold text-neutral-500 flex items-center gap-1.5 mb-2">
                <Search className="w-3.5 h-3.5" /> Find export customer
              </div>
              <div className="relative">
                <Search className="w-4 h-4 text-neutral-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  value={lookupQ}
                  onChange={(e) => setLookupQ(e.target.value)}
                  placeholder="Search by EXP number, name, business, or email"
                  className="w-full text-sm border border-neutral-200 rounded-lg pl-9 pr-3 py-2 focus:outline-none focus:border-[#0078D4]"
                  data-testid="new-export-order-lookup-input"
                  autoFocus
                />
              </div>
              <div className="mt-2 max-h-48 overflow-y-auto rounded-lg border border-neutral-200 divide-y divide-neutral-100" data-testid="new-export-order-lookup-results">
                {lookupBusy && (
                  <div className="p-3 text-xs text-neutral-400">Searching…</div>
                )}
                {!lookupBusy && lookupResults.length === 0 && (
                  <div className="p-3 text-xs text-neutral-400">
                    {lookupQ.trim() ? 'No customer found. Switch to "New customer" to add one.' : 'Start typing to search saved export customers.'}
                  </div>
                )}
                {!lookupBusy && lookupResults.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => pickMaster(c)}
                    className="w-full text-left px-3 py-2 hover:bg-[#DEECF9]/40 flex items-center gap-2"
                    data-testid={`new-export-order-lookup-option-${c.id}`}
                  >
                    <UserCheck className="w-4 h-4 text-[#0078D4] shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-[#0F172A] truncate flex items-center gap-1.5">
                        <span className="font-mono text-[#0078D4]">{c.customer_number}</span>
                        <span className="text-neutral-400">·</span>
                        <CustomerNameLabel customer={c} inline />
                      </div>
                      <div className="text-[11px] text-neutral-500 truncate">
                        {c.email}
                        {c.country_name && <span> · {c.country_name}</span>}
                        {c.phone && <span className="ml-2 font-mono">{c.phone}</span>}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </section>
          )}

          {/* Step 1b — Buyer details form (New mode) OR read-only summary (Existing after pick) */}
          {(buyerMode === 'new' || selectedCustomerId) && (
          <section data-testid="new-export-order-buyer-section" data-mobile-step="0">
            <div className="text-xs uppercase tracking-wider font-bold text-neutral-500 flex items-center gap-1.5 mb-2">
              <User className="w-3.5 h-3.5" />
              {selectedCustomerId ? (
                <>Selected: <span className="font-mono text-[#0078D4]">{selectedCustomerNumber}</span></>
              ) : (
                <>Buyer details</>
              )}
              {selectedCustomerId && (
                <button
                  type="button"
                  onClick={clearMaster}
                  className="ml-auto text-[10px] font-semibold text-[#0078D4] hover:underline inline-flex items-center gap-1"
                  data-testid="new-export-order-change-customer"
                >
                  <X className="w-3 h-3" /> Change
                </button>
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <input
                type="text"
                value={buyer.name}
                onChange={(e) => setBuyer({ ...buyer, name: e.target.value })}
                placeholder="Contact name *"
                disabled={!!selectedCustomerId}
                className="text-sm border border-neutral-200 rounded-lg px-3 py-2 focus:outline-none focus:border-[#0078D4] disabled:bg-neutral-50 disabled:text-neutral-600"
                data-testid="new-export-order-name"
              />
              <input
                type="text"
                value={buyer.business_name}
                onChange={(e) => setBuyer({ ...buyer, business_name: e.target.value })}
                placeholder="Business name (optional)"
                disabled={!!selectedCustomerId}
                className="text-sm border border-neutral-200 rounded-lg px-3 py-2 focus:outline-none focus:border-[#0078D4] disabled:bg-neutral-50 disabled:text-neutral-600"
                data-testid="new-export-order-business"
              />
              <input
                type="email"
                value={buyer.email}
                onChange={(e) => setBuyer({ ...buyer, email: e.target.value })}
                placeholder="Email (optional)"
                disabled={!!selectedCustomerId}
                className="text-sm border border-neutral-200 rounded-lg px-3 py-2 focus:outline-none focus:border-[#0078D4] disabled:bg-neutral-50 disabled:text-neutral-600"
                data-testid="new-export-order-email"
              />
              <input
                type="text"
                value={buyer.address}
                onChange={(e) => setBuyer({ ...buyer, address: e.target.value })}
                placeholder="Delivery address"
                disabled={!!selectedCustomerId}
                className="text-sm border border-neutral-200 rounded-lg px-3 py-2 focus:outline-none focus:border-[#0078D4] disabled:bg-neutral-50 disabled:text-neutral-600"
                data-testid="new-export-order-address"
              />
              <select
                value={countryCode}
                onChange={(e) => onCountryChange(e.target.value)}
                disabled={!!selectedCustomerId}
                className="text-sm border border-neutral-200 rounded-lg px-3 py-2 focus:outline-none focus:border-[#0078D4] bg-white disabled:bg-neutral-50 disabled:text-neutral-600"
                data-testid="new-export-order-country"
              >
                <option value="">Export to * — pick a country</option>
                {EXPORT_COUNTRIES.map((c) => (
                  <option key={c.code} value={c.code}>{c.name} ({c.dial})</option>
                ))}
              </select>
              <input
                type="text"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                disabled={!!selectedCustomerId}
                placeholder={selectedCountry ? `Phone e.g. ${selectedCountry.sample}` : 'Phone (pick a country first for hint)'}
                className="text-sm border border-neutral-200 rounded-lg px-3 py-2 focus:outline-none focus:border-[#0078D4] font-mono disabled:bg-neutral-50 disabled:text-neutral-600"
                data-testid="new-export-order-phone"
              />
            </div>
            {selectedCountry && !selectedCustomerId && (
              <div className="mt-1 text-[11px] text-neutral-500" data-testid="new-export-order-phone-hint">
                Hint: <span className="font-mono">{selectedCountry.sample}</span> (no validation — enter whatever the buyer confirms)
              </div>
            )}
            {buyerMode === 'new' && !selectedCustomerId && (
              <div className="mt-2 text-[11px] text-neutral-500 bg-amber-50 border border-amber-100 rounded-lg px-2 py-1.5 inline-flex items-center gap-1">
                <Globe2 className="w-3 h-3 text-amber-600" />
                A reusable customer number (EXP-XXXXXX) will be assigned on save.
              </div>
            )}
          </section>
          )}

          {/* Step 2 — items — unlocked once buyer core fields ready */}
          {buyerReady && (
            <section data-testid="new-export-order-items-section" data-mobile-step="1">
              <div className="text-xs uppercase tracking-wider font-bold text-neutral-500 flex items-center gap-1.5 mb-2">
                <Package className="w-3.5 h-3.5" /> Items ({cartRows.length})
              </div>
              <div className="relative">
                <Search className="w-4 h-4 text-neutral-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  value={productQuery}
                  onChange={(e) => setProductQuery(e.target.value)}
                  placeholder="Search by item code (SKU) or name"
                  className="w-full text-sm border border-neutral-200 rounded-lg pl-9 pr-3 py-2 focus:outline-none focus:border-[#0078D4]"
                  data-testid="new-export-order-item-search"
                />
              </div>
              <div className="mt-2 max-h-48 overflow-y-auto rounded-lg border border-neutral-200 divide-y divide-neutral-100" data-testid="new-export-order-item-results">
                {filteredProducts.length === 0 && (
                  <div className="p-3 text-xs text-neutral-400" data-testid="new-export-order-item-results-empty">
                    {productQuery.trim()
                      ? `No products match "${productQuery.trim()}" in ${EXPORT_ORDER_CATEGORIES.find((c) => c.slug === orderCategory)?.label || 'this category'}.`
                      : `No products found in ${EXPORT_ORDER_CATEGORIES.find((c) => c.slug === orderCategory)?.label || 'this category'}.`}
                  </div>
                )}
                {filteredProducts.map((p) => {
                  const avail = availableOf(p);
                  const inStock = avail > 0;
                  const packLabel = (p.size_type === 'pack' ? 'Pack' : 'Size');
                  const rowQty = cart[p.id] || 0;
                  // Aug 2026 — resolve effective price for this customer.
                  // When the picked master has a manual price for this
                  // product, show the custom value + a "Custom" chip so
                  // ops sees at a glance which lines are overridden.
                  const pricing = resolvePrice(p, manualPriceMap);
                  return (
                    <div
                      key={p.id}
                      role="button"
                      tabIndex={0}
                      onClick={() => addToCart(p)}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); addToCart(p); } }}
                      className="w-full text-left px-3 py-2 hover:bg-neutral-50 flex items-center gap-2 cursor-pointer"
                      data-testid={`new-export-order-item-option-${p.id}`}
                    >
                      <Plus className="w-4 h-4 text-[#0078D4] shrink-0" />
                      <div className="flex-1 min-w-0">
                        {/* Feb 2026 — Name gets its OWN truncated row so
                            long descriptions never clip the meta chips
                            (Pack / SKU / In hand) that live on the row
                            below. Ensures "In hand" is ALWAYS visible. */}
                        <div
                          className="text-sm font-semibold text-[#0F172A] truncate"
                          title={p.name}
                          data-testid={`new-export-order-item-name-${p.id}`}
                        >
                          {p.name}
                        </div>
                        <div
                          className="mt-1 flex flex-wrap items-center gap-1.5"
                          data-testid={`new-export-order-item-chips-${p.id}`}
                        >
                          {p.size && (
                            <span
                              className="inline-flex items-center rounded-full bg-[#DEECF9] text-[#0078D4] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide"
                              data-testid={`new-export-order-item-pack-${p.id}`}
                            >
                              {packLabel}: {p.size}
                            </span>
                          )}
                          {p.sku && (
                            <span className="font-mono text-[10px] text-neutral-500">{p.sku}</span>
                          )}
                          <span
                            className={`inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-bold tabular-nums whitespace-nowrap shrink-0 ${inStock ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}
                            data-testid={`new-export-order-item-stock-${p.id}`}
                          >
                            In hand: {avail.toLocaleString()}
                          </span>
                        </div>
                        <div className="mt-1 text-[11px] text-neutral-500 tabular-nums">
                          {pricing.currency} {pricing.price.toFixed(3)} / {p.uom || 'ctn'}
                          {pricing.isManual && (
                            <span
                              className="ml-2 inline-flex items-center rounded-full bg-amber-100 text-amber-800 border border-amber-300 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide"
                              title={`Default price: OMR ${pricing.defaultPrice.toFixed(3)}`}
                              data-testid={`new-export-order-picker-custom-${p.id}`}
                            >
                              Custom
                            </span>
                          )}
                          {rowQty > 0 && (
                            <span className="ml-2 text-emerald-700 font-semibold">In cart: {rowQty}</span>
                          )}
                        </div>
                      </div>
                      <input
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        value={rowQty === 0 ? '' : String(rowQty)}
                        onClick={(e) => e.stopPropagation()}
                        onFocus={(e) => { e.stopPropagation(); e.target.select(); }}
                        onChange={(e) => { e.stopPropagation(); setQty(p.id, e.target.value); }}
                        onBlur={(e) => { e.stopPropagation(); commitQty(p.id); }}
                        onKeyDown={(e) => {
                          e.stopPropagation();
                          if (e.key === 'Enter') e.target.blur();
                        }}
                        className="w-16 shrink-0 text-sm text-center border border-neutral-200 rounded-lg px-2 py-1 focus:outline-none focus:border-[#0078D4] tabular-nums font-semibold"
                        data-testid={`new-export-order-picker-qty-${p.id}`}
                        placeholder="qty"
                        aria-label={`Quantity for ${p.name}`}
                      />
                    </div>
                  );
                })}
              </div>

              {cartRows.length > 0 && (
                <div className="mt-4 rounded-xl border border-neutral-200 divide-y divide-neutral-100" data-testid="new-export-order-cart">
                  {cartRowsWithAvail.map((r) => (
                    <div
                      key={r.product.id}
                      className={`p-3 flex items-center gap-3 ${r.over ? 'bg-red-50/40' : ''}`}
                      data-testid={`new-export-order-cart-row-${r.product.id}`}
                    >
                      <div className="flex-1 min-w-0">
                        {/* Feb 2026 — Name gets its own truncated row so
                            long descriptions never clip the meta chips
                            (Pack / SKU / In hand). */}
                        <div
                          className="text-sm font-semibold text-[#0F172A] truncate"
                          title={r.product.name}
                          data-testid={`new-export-order-cart-name-${r.product.id}`}
                        >
                          {r.product.name}
                        </div>
                        <div
                          className="mt-1 flex flex-wrap items-center gap-1.5"
                          data-testid={`new-export-order-cart-chips-${r.product.id}`}
                        >
                          {r.product.size && (
                            <span
                              className="inline-flex items-center rounded-full bg-[#DEECF9] text-[#0078D4] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide"
                              data-testid={`new-export-order-cart-pack-${r.product.id}`}
                            >
                              {r.product.size_type === 'pack' ? 'Pack' : 'Size'}: {r.product.size}
                            </span>
                          )}
                          {r.product.sku && (
                            <span className="font-mono text-[10px] text-neutral-500">{r.product.sku}</span>
                          )}
                          <span
                            className={`inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-bold tabular-nums whitespace-nowrap shrink-0 ${r.avail > 0 ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}
                            data-testid={`new-export-order-cart-stock-${r.product.id}`}
                          >
                            In hand: {r.avail.toLocaleString()}
                          </span>
                        </div>
                        <div className="mt-1 text-[11px] text-neutral-500 tabular-nums">
                          {r.pricing.currency} {r.pricing.price.toFixed(3)} × {r.qty} = <span className="font-bold text-[#0F172A]">{r.pricing.currency} {(r.pricing.price * r.qty).toFixed(3)}</span>
                          {r.pricing.isManual && (
                            <span
                              className="ml-2 inline-flex items-center rounded-full bg-amber-100 text-amber-800 border border-amber-300 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide"
                              title={`Default price: OMR ${r.pricing.defaultPrice.toFixed(3)}`}
                              data-testid={`new-export-order-cart-custom-${r.product.id}`}
                            >
                              Custom
                            </span>
                          )}
                        </div>
                        {r.over && (
                          <div
                            className="mt-1 text-[11px] font-semibold text-amber-800 flex items-center gap-1"
                            data-testid={`new-export-order-cart-shortfall-${r.product.id}`}
                          >
                            <span aria-hidden>⚠</span>
                            {r.avail > 0
                              ? `Exceeds in-hand stock — max available: ${r.avail.toLocaleString()}. Plant will be notified on submit.`
                              : 'Out of stock — plant will be notified on submit.'}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <button
                          type="button"
                          onClick={() => setQty(r.product.id, Math.max(1, r.qty - 1))}
                          className="w-7 h-8 rounded-md border border-neutral-200 bg-white text-neutral-600 hover:bg-neutral-50 flex items-center justify-center"
                          data-testid={`new-export-order-cart-qty-dec-${r.product.id}`}
                          aria-label="Decrease"
                        >
                          −
                        </button>
                        <input
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          value={r.qty === 0 ? '' : String(r.qty)}
                          onFocus={(e) => e.target.select()}
                          onChange={(e) => setQty(r.product.id, e.target.value)}
                          onBlur={() => commitQty(r.product.id)}
                          onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                          className={`w-20 text-sm text-center border rounded-lg px-2 py-1 focus:outline-none tabular-nums font-semibold ${r.over ? 'border-amber-400 bg-amber-50 focus:border-amber-500' : 'border-neutral-200 focus:border-[#0078D4]'}`}
                          data-testid={`new-export-order-cart-qty-${r.product.id}`}
                          placeholder="qty"
                        />
                        <button
                          type="button"
                          onClick={() => setQty(r.product.id, r.qty + 1)}
                          className="w-7 h-8 rounded-md border border-neutral-200 bg-white text-neutral-600 hover:bg-neutral-50 flex items-center justify-center"
                          data-testid={`new-export-order-cart-qty-inc-${r.product.id}`}
                          aria-label="Increase"
                        >
                          +
                        </button>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeFromCart(r.product.id)}
                        className="text-neutral-400 hover:text-red-600 p-1"
                        data-testid={`new-export-order-cart-remove-${r.product.id}`}
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                  <div className="p-3 bg-neutral-50 flex items-center justify-between tabular-nums">
                    <span className="text-xs uppercase tracking-wider font-bold text-neutral-500">Subtotal</span>
                    <span className="text-base font-extrabold text-[#0F172A]" data-testid="new-export-order-subtotal">OMR {subtotal.toFixed(3)}</span>
                  </div>
                  {hasOverCap && (
                    <div
                      className="px-3 py-2 border-t border-amber-200 bg-amber-50 text-[11px] text-amber-900 flex items-start gap-2"
                      data-testid="new-export-order-plant-notice"
                    >
                      <span aria-hidden className="text-base leading-none">⚠</span>
                      <span>
                        <b>{cartRowsWithAvail.filter((x) => x.over).length}</b> line{cartRowsWithAvail.filter((x) => x.over).length === 1 ? '' : 's'} exceed{cartRowsWithAvail.filter((x) => x.over).length === 1 ? 's' : ''} available stock.
                        On submit, the plant team for this order&apos;s category ({
                          EXPORT_ORDER_CATEGORIES.find((c) => c.slug === orderCategory)?.label || orderCategory
                        }) will be automatically notified so production can start.
                      </span>
                    </div>
                  )}
                </div>
              )}
            </section>
          )}
          {!buyerReady && (
            <div className="text-xs text-neutral-500 italic" data-mobile-step="0">
              Pick a product category, then fill in contact name + destination country to unlock item search.
            </div>
          )}

          {/* Step 3 (mobile only) — Ship & Save review summary. Renders
              a compact recap of the buyer, destination, category and
              cart totals so the admin can eyeball the order before
              tapping Submit. Hidden on desktop where every section
              is visible at once. */}
          <section
            data-mobile-step="2"
            className="hidden md:hidden"
            data-testid="new-export-order-review-section"
          >
            <div className="text-xs uppercase tracking-wider font-bold text-neutral-500 mb-2">Review</div>
            <div className="rounded-2xl border border-neutral-200 divide-y divide-neutral-100 bg-white">
              <div className="p-3 flex items-center justify-between gap-3">
                <span className="text-[11px] uppercase tracking-widest text-neutral-500 font-semibold">Buyer</span>
                <span className="text-sm font-semibold text-[#0F172A] text-right truncate">
                  {buyer.business || buyer.name || '—'}
                </span>
              </div>
              <div className="p-3 flex items-center justify-between gap-3">
                <span className="text-[11px] uppercase tracking-widest text-neutral-500 font-semibold">Ship to</span>
                <span className="text-sm font-semibold text-[#0F172A] text-right truncate">
                  {selectedCountry?.name || '—'}
                </span>
              </div>
              <div className="p-3 flex items-center justify-between gap-3">
                <span className="text-[11px] uppercase tracking-widest text-neutral-500 font-semibold">Category</span>
                <span className="text-sm font-semibold text-[#0F172A] text-right truncate">
                  {EXPORT_ORDER_CATEGORIES.find((c) => c.slug === orderCategory)?.label || orderCategory || '—'}
                </span>
              </div>
              <div className="p-3 flex items-center justify-between gap-3">
                <span className="text-[11px] uppercase tracking-widest text-neutral-500 font-semibold">Items</span>
                <span className="text-sm font-semibold text-[#0F172A]">
                  {cartRows.length} line{cartRows.length === 1 ? '' : 's'}
                </span>
              </div>
              <div className="p-3 flex items-center justify-between gap-3 bg-neutral-50 rounded-b-2xl">
                <span className="text-[11px] uppercase tracking-widest text-neutral-500 font-semibold">Subtotal</span>
                <span className="text-base font-extrabold text-[#0F172A] tabular-nums">
                  OMR {subtotal.toFixed(3)}
                </span>
              </div>
            </div>
            {hasOverCap && (
              <div className="mt-2 px-3 py-2 rounded-lg border border-amber-200 bg-amber-50 text-[11px] text-amber-900">
                <b>{cartRowsWithAvail.filter((x) => x.over).length}</b> line
                {cartRowsWithAvail.filter((x) => x.over).length === 1 ? '' : 's'} exceed
                {cartRowsWithAvail.filter((x) => x.over).length === 1 ? 's' : ''} available stock. Plant will be notified on submit.
              </div>
            )}
          </section>
        </div>

        {/* Desktop bottom action bar (unchanged) */}
        <div className="hidden md:flex p-4 border-t border-neutral-100 sticky bottom-0 bg-white items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="text-xs font-semibold text-neutral-600 hover:text-neutral-900 px-3 py-2 rounded-full border border-neutral-300 hover:bg-neutral-50 disabled:opacity-50"
            data-testid="new-export-order-cancel-btn"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={!canSubmit}
            className={`text-xs font-bold text-white px-4 py-2 rounded-full disabled:opacity-40 inline-flex items-center gap-1.5 ${hasOverCap ? 'bg-amber-600 hover:bg-amber-700' : 'bg-[#0078D4] hover:bg-[#005A9E]'}`}
            data-testid="new-export-order-submit-btn"
          >
            {submitting
              ? 'Creating…'
              : cartRows.length === 0
                ? 'Add items to submit'
                : hasOverCap
                  ? `Submit & notify plant · ${cartRows.length} item${cartRows.length === 1 ? '' : 's'}`
                  : `Submit · ${cartRows.length} item${cartRows.length === 1 ? '' : 's'}`}
          </button>
        </div>

        {/* Mobile wizard fixed bottom action bar — Back / Next / Submit */}
        <div
          className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-white/95 backdrop-blur border-t border-neutral-200 px-4 py-3 flex items-center gap-3 shadow-[0_-6px_20px_-8px_rgba(15,23,42,0.15)] pb-[max(env(safe-area-inset-bottom,0),12px)]"
          data-testid="new-export-order-mobile-actions"
        >
          <button
            type="button"
            onClick={() => (mobileStep === 0 ? onClose() : setMobileStep(mobileStep - 1))}
            className="flex-1 h-12 rounded-full border border-neutral-300 text-[#0F172A] text-sm font-bold active:bg-neutral-100"
            data-testid="new-export-order-mobile-back"
          >
            {mobileStep === 0 ? 'Cancel' : 'Back'}
          </button>
          <button
            type="button"
            onClick={() => {
              if (mobileStep < 2) {
                if (mobileCanAdvance) setMobileStep(mobileStep + 1);
                return;
              }
              submit();
            }}
            disabled={!mobileCanAdvance || submitting}
            data-testid="new-export-order-mobile-next"
            className={`flex-1 h-12 rounded-full text-white text-sm font-bold transition-colors disabled:opacity-50 ${hasOverCap && mobileStep === 2 ? 'bg-amber-600 active:bg-amber-700' : 'bg-[#0078D4] active:bg-[#005A9E]'}`}
          >
            {submitting
              ? 'Submitting…'
              : mobileStep < 2
                ? 'Next'
                : hasOverCap
                  ? 'Submit & notify plant'
                  : `Submit · ${cartRows.length}`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default NewExportOrderModal;
